import { cloneElement } from "react";
import { Field as ChakraField, Input, Textarea, Switch, Select, Text, Box, Portal, createListCollection } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { getIn } from "formik";

export const isFieldInvalid = (formik, path) => Boolean(getIn(formik.errors, path));

/** True when a required field is still incomplete on a step the user has already left. */
export const isFieldWarning = (warningErrors, visited, path) => visited && Boolean(getIn(warningErrors, path));

const stateBorderColor = (invalid, warning) => (invalid ? "red.400" : warning ? "orange.400" : undefined);

export const Field = ({ label, required, invalid, warning, hint, children }) => {
    const borderColor = stateBorderColor(invalid, warning);
    return (
        <ChakraField.Root>
            <ChakraField.Label fontSize="sm" fontWeight={600} color={invalid ? "red.600" : "gray.800"}>
                {label}
                {required && <Text as="span" color={invalid ? "red.600" : "purple.500"} fontWeight={700}> *</Text>}
            </ChakraField.Label>
            {borderColor ? cloneElement(children, { borderColor }) : children}
            {hint && <ChakraField.HelperText fontSize="xs" color="gray.500">{hint}</ChakraField.HelperText>}
        </ChakraField.Root>
    );
};

// Chrome/Edge/Safari all apply the mouse wheel to a focused <input type="number">'s value
// instead of scrolling the page -- surprising and easy to trigger by accident (scrolling
// past a numeric field without deliberately clicking out of it first, silently changing its
// value). Blurring on wheel restores normal page-scroll behavior; it only fires for number
// inputs, every other type is left untouched.
const blurNumberOnWheel = (e) => {
    if (e.currentTarget.type === "number") e.currentTarget.blur();
};

export const FormInput = ({ onWheel, ...props }) => (
    <Input
        bg="white"
        borderColor="gray.200"
        color="gray.900"
        fontSize="sm"
        transition="border-color 0.15s, box-shadow 0.15s"
        _placeholder={{ color: "gray.400" }}
        _hover={{ borderColor: "gray.300" }}
        _focus={{ borderColor: "purple.400", boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)" }}
        onWheel={(e) => { blurNumberOnWheel(e); onWheel?.(e); }}
        {...props}
    />
);

export const FormTextarea = ({ rows = 3, ...props }) => (
    <Textarea
        rows={rows}
        bg="white"
        borderColor="gray.200"
        color="gray.900"
        fontSize="sm"
        resize="none"
        transition="border-color 0.15s, box-shadow 0.15s"
        _placeholder={{ color: "gray.400" }}
        _hover={{ borderColor: "gray.300" }}
        _focus={{ borderColor: "purple.400", boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)" }}
        {...props}
    />
);

export const AdditionalInfoField = ({ value, onChange, rows = 2, ...props }) => {
    const { t } = useTranslation("jobOffer");
    return (
        <Field label={t("additionalInfo.label")}>
            <FormTextarea
                value={value ?? ""}
                onChange={onChange}
                placeholder={t("additionalInfo.placeholder")}
                rows={rows}
                {...props}
            />
        </Field>
    );
};

export const FormSelect = ({ value, onChange, options, placeholder, multiple = false, borderColor = "gray.200" }) => {
    const collection = createListCollection({ items: options });
    const selected = value ? (Array.isArray(value) ? value : [value]) : [];
    const isEmpty = selected.length === 0;
    return (
        <Select.Root
            collection={collection}
            value={selected}
            multiple={multiple}
            onValueChange={(e) => onChange(multiple ? e.value : e.value[0] ?? "")}
        >
            <Select.HiddenSelect />
            <Select.Control>
                <Select.Trigger
                    bg="white"
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="l2"
                    h="10"
                    fontSize="sm"
                    transition="border-color 0.15s, box-shadow 0.15s"
                    _hover={{ borderColor: "gray.300" }}
                    _focus={{ borderColor: "purple.400", boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)" }}
                >
                    <Select.ValueText placeholder={placeholder} color={isEmpty ? "gray.400" : "gray.900"} />
                </Select.Trigger>
                <Select.IndicatorGroup>
                    <Select.Indicator />
                </Select.IndicatorGroup>
            </Select.Control>
            <Portal>
                <Select.Positioner>
                    <Select.Content bg="white" borderColor="gray.200" boxShadow="0 4px 24px rgba(23,21,31,0.12)">
                        {options.map((opt) => (
                            <Select.Item item={opt} key={opt.value} color="gray.900" _hover={{ bg: "purple.50" }}>
                                {opt.label}
                                <Select.ItemIndicator />
                            </Select.Item>
                        ))}
                    </Select.Content>
                </Select.Positioner>
            </Portal>
        </Select.Root>
    );
};

export const FormSwitch = ({ label, checked, onChange, description }) => (
    <Switch.Root w="full" checked={checked} onCheckedChange={(e) => onChange(e.checked)}>
        <Switch.HiddenInput />
        <Switch.Control flexShrink={0} bg={checked ? "purple.500" : "gray.300"}>
            <Switch.Thumb />
        </Switch.Control>
        <Switch.Label flex={1} minW={0}>
            <Text fontSize="sm" color="gray.800" wordBreak="normal" overflowWrap="break-word">{label}</Text>
            {description && <Text fontSize="xs" color="gray.500" wordBreak="normal" overflowWrap="break-word">{description}</Text>}
        </Switch.Label>
    </Switch.Root>
);

export const FormCheckbox = ({ label, checked, onChange }) => (
    <Box as="label" display="flex" alignItems="center" gap={2} cursor="pointer">
        <Box
            as="input"
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            w={4}
            h={4}
            accentColor="purple.500"
            cursor="pointer"
            flexShrink={0}
        />
        <Text fontSize="sm" color="gray.800">{label}</Text>
    </Box>
);

export const WeightField = ({ value, onChange, label }) => {
    const { t } = useTranslation("jobOffer");
    return (
    <Field label={label ?? t("scoringPanel.weight")} hint={t("scoringPanel.weightHint")}>
        <FormInput
            type="number"
            min="0"
            max="10"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
        />
    </Field>
    );
};
